import PropTypes from 'prop-types'
import {Box, CircularProgress, Typography} from '@mui/material'
import {useEffect, useState, useMemo, useRef} from 'react'
import {useQuery, gql} from '@apollo/client'
import FilterHeader from '../FilterHeader/FilterHeader'
import CheckboxGroup from '../CheckboxGroup'
import {useStatesAndHealthConditions} from '../../../store/hooks/breadCrumbs'
import {useSelector} from 'react-redux'
import {selectFilterConfig} from '../../../store/slices/configSlice'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'

/**
 * CheckboxFilterControl - FilterHeader + Checkbox Group Component with Dynamic Fetching
 *
 * A composable component that combines FilterHeader with an independent checkbox group.
 * Separates header logic from checkbox logic for maximum reusability.
 *
 * Features:
 * - Dynamic option fetching when options array is empty
 * - Supports both regular filters and geographic indicator filters (via categoryId)
 * - Integrates with filterConfig.config.json structure
 */
const CheckboxFilterControl = ({
  label = 'Filter',
  description,
  options = [],
  selectedValues = [],
  onChange,
  onRemove,
  showRemoveButton = true,
  placeholder,
  showChips = true,
  showSelectAll = true,
  selectAllLabel = 'Select All',
  maxHeight = '70vh',
  filterKey,
  categoryId,
  ...rest
}) => {
  const {filters} = useStatesAndHealthConditions()
  const filterConfig = useSelector(selectFilterConfig)
  const [fetchedOptions, setFetchedOptions] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const {getPendingFilterValue, clearPendingFilterValue, setFilterRangeOptions, getFilterRangeOptions} =
    useFilterManagement()
  const pendingValueAppliedRef = useRef(false)

  const needsDataFetching = options.length === 0 && filterKey

  const emptyArray = useMemo(() => [], [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedState = useMemo(() => filters?.[0]?.default || emptyArray, [filters])
  const stateId = useMemo(
    () =>
      selectedState
        .map((stateName) => {
          const stateObj = filters?.[0]?.values?.find((state) => state.STATE_NAME === stateName)
          return stateObj?.STATE_ID
        })
        .filter(Boolean),
    [selectedState, filters]
  )

  const isGeographicIndicator = categoryId === '6' || filterConfig?.specialGeographicIndicators?.includes(filterKey)

  // Check if we already have data in Redux to skip query
  const storedOptionsForSkip = getFilterRangeOptions(filterKey)
  const hasStoredData = storedOptionsForSkip?.checkboxOptions && storedOptionsForSkip.checkboxOptions.length > 0

  const fetchOptionsQuery = gql`
    query CheckboxOptions${filterKey} {
      sdohValues(key: "${filterKey}") {
        OPTION_NAME
        OPTION_INDEX
      }
    }
  `

  const fetchGeographicOptions = gql`
    query CheckboxGeoOptions${filterKey}($state_id: [Int]) {
      sdohGeographicalIndicatorValues(key: "${filterKey}", stateId: $state_id) {
        OPTION_NAME
        OPTION_INDEX
        STATE_NAME
      }
    }
  `

  const {data, loading} = useQuery(isGeographicIndicator ? fetchGeographicOptions : fetchOptionsQuery, {
    skip: !needsDataFetching || hasStoredData, // Skip if data already in Redux
    variables: isGeographicIndicator ? {state_id: stateId} : undefined,
    onError: (error) => {
      console.error(`Error fetching options for ${filterKey}:`, error)
      setIsFetching(false)
    }
  })

  const sortOptions = (optionsToSort) => {
    const sorted = [...optionsToSort]
    sorted.sort((a, b) => {
      const str1 = a.label.split('-')[0]
      const str2 = b.label.split('-')[0]
      if (str1 === 'Less' && str2 !== 'Less') return -1
      if (str2 === 'Less' && str1 !== 'Less') return 1
      return Number.parseFloat(str1.replaceAll(/\D/g, '')) - Number.parseFloat(str2.replaceAll(/\D/g, ''))
    })
    return sorted
  }

  const formatGeographicOptions = (data) => {
    const shouldShowState = selectedState.length > 1 && !filterConfig?.disableStateSeperation?.includes(filterKey)
    const isSpecialIndicator = filterConfig?.specialGeographicIndicators?.includes(filterKey)
    return data.map((element) => ({
      label: shouldShowState ? `${element.OPTION_NAME}, ${element.STATE_NAME}` : element.OPTION_NAME,
      value: isSpecialIndicator ? `${element.OPTION_INDEX}` : `${element.OPTION_NAME}`
    }))
  }

  const formatStandardOptions = (data) => {
    let formatted = data.map((element) => ({
      label: element.OPTION_NAME,
      value: element.OPTION_INDEX === null ? element.OPTION_NAME : element.OPTION_INDEX
    }))

    if (formatted.some((opt) => opt.label.includes('-') || opt.label.includes('Less'))) {
      formatted = sortOptions(formatted)
    }
    return formatted
  }

  useEffect(() => {
    if (!needsDataFetching) return
    if (isInitialized) return

    // Check if we already have stored options in Redux
    const storedOptions = getFilterRangeOptions(filterKey)
    if (storedOptions?.checkboxOptions && storedOptions.checkboxOptions.length > 0) {
      setFetchedOptions(storedOptions.checkboxOptions)
      setIsFetching(false)
      setIsInitialized(true)
      return
    }

    // Fetch from API if not in Redux
    if (!loading && data && !hasStoredData) {
      setIsFetching(true)

      let formattedOptions = []
      if (isGeographicIndicator && data.sdohGeographicalIndicatorValues) {
        formattedOptions = formatGeographicOptions(data.sdohGeographicalIndicatorValues)
      } else if (data.sdohValues) {
        formattedOptions = formatStandardOptions(data.sdohValues)
      }

      setFetchedOptions(formattedOptions)
      if (filterKey && formattedOptions.length > 0) {
        setFilterRangeOptions(filterKey, {checkboxOptions: formattedOptions})
      }
      setIsFetching(false)
      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, needsDataFetching, isGeographicIndicator, selectedState, filterKey, filterConfig, isInitialized])

  useEffect(() => {
    if (isGeographicIndicator && options.length === 0 && filterKey && fetchedOptions.length > 0) {
      setFetchedOptions([])
      setIsInitialized(false)
      // Clear stored options when state changes for geographic indicators
      setFilterRangeOptions(filterKey, {checkboxOptions: []})
      pendingValueAppliedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, isGeographicIndicator, filterKey, setFilterRangeOptions])

  useEffect(() => {
    if (fetchedOptions.length > 0 && !pendingValueAppliedRef.current && filterKey) {
      const pendingValue = getPendingFilterValue(filterKey)

      if (pendingValue !== undefined) {
        clearPendingFilterValue(filterKey)
        if (onChange) {
          onChange(pendingValue)
        }
        pendingValueAppliedRef.current = true
      }
    }
  }, [fetchedOptions, filterKey, getPendingFilterValue, clearPendingFilterValue, onChange])

  const effectiveOptions = options.length > 0 ? options : fetchedOptions
  const isLoading = needsDataFetching && (loading || isFetching)

  return (
    <Box width='100%'>
      <FilterHeader
        label={
          <Box display='flex' alignItems='center' gap={1}>
            <Typography component='span'>{label}</Typography>
            {isLoading && <CircularProgress size={15} />}
          </Box>
        }
        description={description}
        onRemove={onRemove}
        showRemoveButton={showRemoveButton}
      />
      {isLoading && effectiveOptions.length === 0 ? (
        <Box py={2}>
          <Typography variant='body2' color='text.secondary'>
            Loading options...
          </Typography>
        </Box>
      ) : (
        <CheckboxGroup
          options={effectiveOptions}
          selectedValues={selectedValues}
          onChange={onChange}
          placeholder={placeholder || `(${effectiveOptions.length} Values)`}
          showChips={showChips}
          showSelectAll={showSelectAll}
          selectAllLabel={selectAllLabel}
          maxHeight={maxHeight}
          ariaLabel={label}
          {...rest}
        />
      )}
    </Box>
  )
}

CheckboxFilterControl.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ),
  selectedValues: PropTypes.array,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  placeholder: PropTypes.string,
  showChips: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  maxHeight: PropTypes.string,
  filterKey: PropTypes.string,
  categoryId: PropTypes.string
}

export default CheckboxFilterControl
